import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { apiClient } from '@/core/api/client';
import { useConciergeChooser } from '@/features/care-concierge/chooser/ConciergeChooserProvider';
import { useToast } from '@/shared/components/Toast';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import {
  BestMatchHighlight,
  findBestMatchEntry,
  KeyResourcesPanel,
  KeyResourcesPanelSkeleton,
  NavigatorResults,
  NavigatorResultsStepper,
  navigatorResultsCopy,
  swapSelection,
  useNavigatorMatching,
} from '@/shared/components/navigator-results';
import type {
  FpQaResourceBundle,
  NavigatorCategory,
  NavigatorProvider,
} from '@/shared/components/navigator-results';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { useGuestActionGate } from '@/shared/hooks/useGuestActionGate';
import { CareTeamMessageGate } from '@/features/chatbot/components/CareTeamMessageGate';
import { RequestCallModal } from '@/features/consultations/components/RequestCallModal';
import { resolveBestMatches } from '@/shared/components/navigator-results/prototype/bestMatchPrototype.data';
import { MOCK_CARE_GUIDE_VIEWMODEL } from '@/shared/components/navigator-results/prototype/mockCareGuide';
import { useBestMatchPrototype } from '@/shared/components/navigator-results/prototype/useBestMatchPrototype';
import { BestMatchPrototypeSwitcher } from '@/shared/components/navigator-results/prototype/BestMatchPrototypeSwitcher';
import { VariantCSpotlightStrip } from '@/shared/components/navigator-results/prototype/VariantCSpotlightStrip';
import { CareSeekerConciergeBanner } from './CareSeekerConciergeBanner';
import { CareSeekerResultsActions } from './CareSeekerResultsActions';
import { buildCareSeekerResultsViewModel } from '../utils/careSeekerResultsAdapter';
import type { CareSeekerSuggestedResource } from '../utils/careSeekerResultsAdapter';
import { formatCareStageLabel } from '../utils/formatCareStage';

type ResultsLocationState = {
  sessionId?: string;
  careStage?: string;
  summaryBullets?: string[];
};

type SuggestedResourcesResponse = { resources?: CareSeekerSuggestedResource[] };

export const CareSeekerResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const state = (location.state as ResultsLocationState | null) ?? {};
  const copy = navigatorResultsCopy['care-seeker'];

  const sessionId = state.sessionId ?? sessionStorage.getItem('bquest_nav_session_id') ?? undefined;
  const careStage = state.careStage ?? sessionStorage.getItem('bquest_nav_care_stage') ?? '';
  const summaryBullets = state.summaryBullets ?? [];
  // Without a sessionId there is nothing to build — direct nav or a refresh that
  // dropped the session would otherwise hang on "Building your care plan…".
  const hasSession = Boolean(sessionId);

  // Store-backed (not a standalone poll): attaches to the shared generation
  // store so a direct/reload arrival here still populates the store swap
  // selection reads from — parity with the FP results page (feature 080 bug:
  // the previous standalone poller left Select silently no-oping whenever
  // this page was reached without going through the chat/loading flow first).
  const { status, categories } = useNavigatorMatching(sessionId);
  const isMatching = status === 'idle' || status === 'running';
  // Feature 102: "Request a call" replaces the old AI-drafted consultation
  // request as the top-match primary action.
  const [callRequestProvider, setCallRequestProvider] = useState<NavigatorProvider | null>(null);
  // Care Concierge modal for the empty-category handoff (same modal the
  // footer banner opens internally).
  const { openConcierge } = useConciergeChooser();

  const { isAuthenticated } = useAuth();
  const { openProviderContactGate } = useGuestActionGate();

  const { data: resources = [] } = useQuery<CareSeekerSuggestedResource[]>({
    queryKey: ['care-seeker-suggested-resources', sessionId],
    enabled: status === 'ready',
    staleTime: Infinity,
    queryFn: async () => {
      // Guests reach this page too; opt out of the global 401 -> /login bounce
      // and just degrade to an empty resource list when unauthenticated.
      const { data } = await apiClient.get<SuggestedResourcesResponse>(
        '/resources/suggested-resources',
        { params: { session_id: sessionId }, skipAuthRedirect: true },
      );
      return data.resources ?? [];
    },
  });

  // Feature 079: curated specialty + care-stage Key Resources — the same bundle
  // the FP results page shows (specialties_resources + care_stage_resources),
  // keyed off the navigator session so the careseeker sees the resource tied to
  // each recommended specialty type, just like the FP.
  const { data: hubResources, isLoading: hubResourcesLoading } = useQuery<FpQaResourceBundle>({
    queryKey: ['care-seeker-hub-resources', sessionId],
    enabled: status === 'ready' && Boolean(sessionId),
    staleTime: Infinity,
    queryFn: async () => {
      const { data } = await apiClient.get<FpQaResourceBundle>('/find-providers/hub-resources', {
        params: { session_id: sessionId },
        skipAuthRedirect: true,
      });
      return data;
    },
  });

  const viewModel = buildCareSeekerResultsViewModel({
    careStageLabel: formatCareStageLabel(careStage),
    summaryBullets,
    categories,
    resources,
  });

  // PROTOTYPE (087 ticket 10) — enabled with ?bestMatchPrototype=A.
  // Falls back to a mocked care guide shell when there's no live session
  // (expired, or never started one) so the redesign can still be reviewed —
  // a real session's categories are always preferred when present.
  const bestMatchProto = useBestMatchPrototype();
  const usingMockCareGuide = bestMatchProto.enabled && !hasSession;
  const baseViewModel = usingMockCareGuide ? MOCK_CARE_GUIDE_VIEWMODEL : viewModel;
  const bestMatch = bestMatchProto.enabled
    ? resolveBestMatches(baseViewModel, bestMatchProto.scenario)
    : undefined;
  const displayViewModel = bestMatch?.viewModel ?? baseViewModel;
  // Only hold the skeleton while there is genuinely nothing to show. Once the
  // specialty list has landed the guide renders and providers fill in per row,
  // so the reader sees the care team long before matching finishes. Gated on
  // the view model that actually renders, so a Best Match substitution cannot
  // leave the page stuck on the skeleton.
  const isBuilding = hasSession && isMatching && displayViewModel.categories.length === 0;

  // Feature 087 (ticket 15) — production Best Match, computed server-side on
  // `viewModel` (the real session data, never the prototype's mock/fixture
  // shell). Independent of the dev-only prototype override above.
  const liveBestMatch = findBestMatchEntry(viewModel);

  // Messaging and requesting a consultation are account-only: both lead to
  // JWT-guarded flows (and /messages is a protected route that would bounce a
  // guest to /login). For guests these provider-contact actions pop the signup
  // modal instead — matching the public SP profile gate (feature 076-03) and
  // deep-linking back to the provider after signup.
  const gateGuestContact = (providerId: string) =>
    openProviderContactGate({ providerId, providerType: 'SERVICE_PROVIDER' });

  const handleRequestCall = (provider: NavigatorProvider) => {
    if (!isAuthenticated) {
      gateGuestContact(provider.id);
      return;
    }
    setCallRequestProvider(provider);
  };

  // Feature 080 — single-select swap: promote an alternate to the specialty's
  // selection. Optimistic in the store (guest-safe POST); on failure the store
  // reverts on its own, so the UI simply settles back.
  const handleSelectProvider = async (provider: NavigatorProvider, category: NavigatorCategory) => {
    if (!sessionId) return;
    const ok = await swapSelection(sessionId, category.id, provider.id);
    if (!ok) showToast(copy.selectionSaveFailed, 'error');
  };

  // Top-match action buttons mirror the FP MatchedProviderCard pills (sage fill /
  // sage outline / ghost). The primary differs by role: the care seeker requests
  // a consultation for themselves where the FP refers a provider to a client.
  const renderProviderActions = (provider: NavigatorProvider) => (
    <>
      <Button
        size="sm"
        onClick={() => handleRequestCall(provider)}
        className="h-9 rounded-full bg-sage px-4 text-sm text-white hover:bg-[hsl(169_95%_18%)]"
      >
        Request a call
      </Button>
      <CareTeamMessageGate
        providerId={provider.id}
        providerName={provider.name}
        sourceSurface="navigator"
        isGuest={!isAuthenticated}
        onGuestGate={() => gateGuestContact(provider.id)}
      >
        {(onMessage) => (
          <Button
            size="sm"
            variant="outline"
            onClick={onMessage}
            className="h-9 rounded-full border-sage bg-transparent px-4 text-sm text-sage hover:bg-sage-bg hover:text-sage"
          >
            Message
          </Button>
        )}
      </CareTeamMessageGate>
      <Button
        size="sm"
        variant="ghost"
        asChild
        className="h-9 rounded-full px-2 text-sm font-medium text-text-secondary hover:text-sage"
      >
          <a
            href={`/providers/${provider.id}?discoverySource=NAVIGATOR`}
            target="_blank"
            rel="noopener noreferrer"
          >
          View profile
        </a>
      </Button>
    </>
  );

  const backSlot = (
    <button
      type="button"
      onClick={() => navigate('/care-navigator')}
      className="inline-flex items-center gap-1 text-sm text-sage transition-colors duration-150 ease-out hover:text-[hsl(169_95%_18%)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden />
      Back to Navigator
    </button>
  );


  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Full container width (no max-w cap) to match the wider FP results page. */}
        <div className="w-full">
          {!hasSession && !usingMockCareGuide ? (
            <div className="space-y-6">
              {backSlot}
              <div className="rounded-2xl border border-dashed border-border-subtle bg-card-subtle p-8 text-center">
                <p className="font-termina text-xl font-medium text-text-primary">
                  This care guide session has expired
                </p>
                <p className="mt-2 text-sm text-text-secondary">
                  Head back to the Navigator to start a new one.
                </p>
              </div>
            </div>
          ) : isBuilding ? (
            <div className="space-y-6">
              {backSlot}
              <p className="font-termina text-xl font-medium text-text-primary">
                Building your care guide…
              </p>
              <Skeleton className="h-32 w-full rounded-2xl" />
              <Skeleton className="h-48 w-full rounded-2xl" />
            </div>
          ) : (
            <NavigatorResults
              viewModel={displayViewModel}
              isMatching={isMatching}
              copy={copy}
              topSlot={
                <>
                  {backSlot}
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <NavigatorResultsStepper step2Label="Your Care Guide" />
                  </div>
                </>
              }
              beforeTabsSlot={
                bestMatchProto.enabled && bestMatch ? (
                  <VariantCSpotlightStrip result={bestMatch} />
                ) : liveBestMatch ? (
                  <BestMatchHighlight
                    entry={liveBestMatch}
                    actions={renderProviderActions(liveBestMatch.provider)}
                  />
                ) : undefined
              }
              footerSlot={
                <CareSeekerConciergeBanner onContactClick={() => openConcierge('navigator-banner')} />
              }
              headerActions={
                <CareSeekerResultsActions
                  sessionId={sessionId}
                  planLabel={displayViewModel.planTitle}
                  isMatching={isMatching}
                />
              }
              onConciergeRequest={() => openConcierge('navigator-banner')}
              onSelectProvider={handleSelectProvider}
              renderProviderActions={renderProviderActions}
              bestMatchProviderIds={bestMatchProto.enabled ? bestMatch?.providerIds : undefined}
              renderKeyResources={
                hubResourcesLoading
                  ? () => <KeyResourcesPanelSkeleton />
                  : hubResources
                    ? (category) => (
                        <KeyResourcesPanel
                          specialtyResources={
                            hubResources.specialtyResourcesBySpecialtyId?.[category.id] ?? []
                          }
                          careStageResources={hubResources.careStageResources ?? []}
                        />
                      )
                    // Bundle settled without data (disabled/errored) — fall back to
                    // NavigatorResults' default panel (AI suggested resources)
                    // instead of rendering nothing.
                    : undefined
              }
            />
          )}
        </div>
      </div>


      {callRequestProvider && (
        <RequestCallModal
          open
          onOpenChange={(open) => {
            if (!open) setCallRequestProvider(null);
          }}
          providerId={callRequestProvider.id}
          providerName={callRequestProvider.name}
        />
      )}

      {bestMatchProto.enabled && <BestMatchPrototypeSwitcher controls={bestMatchProto} />}
    </div>
  );
};
